// client/src/pages/sections/groups/toast.js
// Tiny module-level pub/sub toast bus. Any component can call emitToast()
// without needing a provider; a single <ToastViewport/> subscribes and
// renders the live list. Used by the Client Groups action components to
// surface optimistic-revert errors after a modal has already closed.
//
//   emitToast({ kind: 'error'|'success'|'info', message, durationMs })
//   useToasts()  — hook returning the current array (for the viewport)
//   dismissToast(id)  — manual dismiss (the X button on a toast)

import { useEffect, useState } from 'react';

const listeners = new Set();
let nextId = 0;
let toasts = [];

function notify() {
  const snapshot = toasts.slice();
  for (const l of listeners) l(snapshot);
}

// Errors stay up long enough to read (10 s) unless the caller says otherwise.
export function emitToast({ kind = 'info', message, durationMs } = {}) {
  if (!message) return null;
  if (durationMs === undefined) durationMs = kind === 'error' ? 10000 : 5000;
  const id = ++nextId;
  toasts.push({ id, kind, message });
  notify();
  if (durationMs > 0) {
    setTimeout(() => dismissToast(id), durationMs);
  }
  return id;
}

// Human-readable text from a failed request. The API answers errors as JSON
// `{ error }` (httpJson.js) — postResolveAction keeps the raw body on
// err.message / err.body — so unwrap it instead of toasting a JSON blob.
export function errorText(err) {
  const raw = (err && (err.body || err.message)) || String(err || '');
  try {
    const j = JSON.parse(raw);
    if (j && typeof j === 'object') {
      const m = j.error || j.message;
      if (typeof m === 'string' && m) return m;
      if (m && typeof m.message === 'string') return m.message;
    }
  } catch { /* not JSON — use as is */ }
  return (err && err.message) || raw;
}

export function dismissToast(id) {
  const next = toasts.filter(t => t.id !== id);
  if (next.length === toasts.length) return;
  toasts = next;
  notify();
}

export function useToasts() {
  const [list, setList] = useState(toasts);
  useEffect(() => {
    listeners.add(setList);
    return () => { listeners.delete(setList); };
  }, []);
  return list;
}
